import { faArrowToBottom, faArrowToTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import {
    IIntegrationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { IntegrationUploadHistoryPagedDataSource } from 'custom_hooks/data_hooks/data_source/integrationUploadHistoryDataSource';
import { useTreeItems } from 'custom_hooks/data_hooks/tree/useTreeItems';
import { ExportButtonSpinner } from 'pages/Reports/button/ExportReportButton';
import { useMemo } from 'react';
import { downloadFileResponse, FileType, uploadFile } from 'utils/file';
import { uploadIntegrationMappingFile } from 'utils/integration_mapping';

export type MappingButtonsProps = Readonly<{
    location: ILocationResponse;
    integration: IIntegrationResponse;
    dataSource: IntegrationUploadHistoryPagedDataSource;
}>;

export function MappingButtons({
    location,
    integration,
    dataSource: { reset, reloadPage, isLoading },
}: MappingButtonsProps) {
    const { showToast } = useNotificationContext();

    const rootItemIds = useMemo(() => location.Systems.map(({ Id }) => Id), [
        location.Systems,
    ]);
    const { data: treeItems } = useTreeItems(rootItemIds);
    const hasNoTreeItems = treeItems === undefined || treeItems.length === 0;

    const { mutate: downloadMappings, isPending: isDownloadingMappings } = useMutation({
        mutationFn: async () => {
            const response = await sdClient.getMappingTemplate({
                id: location.Id,
                integrationId: integration.Id,
            });
            downloadFileResponse({
                response,
                defaultFileName:
                    response.fileName ??
                    `${integration.IntegrationType.toUpperCase()}_Mapping_Template_${location.Name.replace(
                        / /g,
                        '_'
                    )}`,
                fileType: FileType.Excel,
            });
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Failed to download mappings file',
            });
        },
    });

    const { mutate: uploadMappings, isPending: isUploadingMappings } = useMutation({
        mutationFn: (file: File) =>
            uploadIntegrationMappingFile({
                locationId: location.Id,
                integration,
                file,
            }),

        onSuccess: () => {
            reset();
            reloadPage();
            showToast({
                type: 'success',
                content: 'Successfully uploaded mappings file',
            });
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Error uploading mappings file',
            });
        },
    });

    const onClickUpload = () => {
        uploadFile(
            (uploadedFile: File) => {
                uploadMappings(uploadedFile);
            },
            ['.xlsx', '.csv']
        );
    };

    const isDisabled =
        isDownloadingMappings || isUploadingMappings || hasNoTreeItems || isLoading;

    return (
        <div className="ml-auto ">
            <Button
                className="mr-10"
                onClick={() => downloadMappings()}
                disabled={isDisabled}
                title={
                    hasNoTreeItems
                        ? 'Location has no tree items below the system level'
                        : 'Download Mappings'
                }
                icon={
                    isDownloadingMappings ? (
                        <ExportButtonSpinner className="inline-block" size="1x" />
                    ) : (
                        <FontAwesomeIcon icon={faArrowToBottom} />
                    )
                }
            >
                Download Mappings
            </Button>
            <Button
                onClick={onClickUpload}
                disabled={isDisabled}
                title={
                    hasNoTreeItems
                        ? 'Location has no tree items below the system level'
                        : 'Upload Mappings'
                }
                icon={
                    isUploadingMappings ? (
                        <ExportButtonSpinner className="inline-block" size="1x" />
                    ) : (
                        <FontAwesomeIcon icon={faArrowToTop} />
                    )
                }
            >
                Upload Mappings
            </Button>
        </div>
    );
}
